import moment from 'moment';
import PropTypes from 'prop-types';

import { alpha, useTheme } from '@mui/material/styles';
import { Stack, TableRow, TableCell, Typography, Chip } from '@mui/material';
import FileThumbnail from '../../../atoms/FileThumbnail';
import { getApprovalStatus, getFileNameFromPath } from '../DocumentsView/helpers/documentsHelpers';
import DemandStatus from './DemandStatus';
import DocumentAction from './DocumentAction';

import { useBreakpoints } from '../../../../hooks/useBreakpoints';

/**
 * Component to render rows for a table of documents.
 * Each row includes a file thumbnail, document information, and possible actions.
 *
 * @param {Object} props - Component props
 * @param {Array} props.files - Array of files to display
 * @param {Function} props.handleClickFile - Callback to handle when a file row is clicked
 * @param {Function} props.handleOpenModalConfirmCancellation - Callback to open a modal to confirm cancellation
 * @param {number} props.rowsPerPage - Number of rows to display per page
 * @param {number} props.page - Current page number
 * @param {Function} props.setSelectedFile - Callback to set the selected file
 * @param {boolean} props.showOwner - Flag to show or hide file's owner data
 * @param {boolean} props.showPagination - Flag to show or hide pagination
 */
const DocumentsRows = (props) => {
  const {
    files,
    handleClickFile,
    handleOpenModalConfirmCancellation,
    rowsPerPage,
    page,
    setSelectedFile,
    showOwner,
    showPagination
  } = props;

  const { palette } = useTheme();
  const { isTablet } = useBreakpoints();

  const hasAtLeastOnePendingDemand = files.some((file) => getApprovalStatus(file));
  const hightDensity = !showOwner || isTablet;

  return files
    .slice(page * rowsPerPage, showPagination ? page * rowsPerPage + rowsPerPage : files.length)
    .map((file, idx) => {
      const dateCreation = file?.created_at ?? null;
      const extension = file?.extension?.toLowerCase() ?? '';
      const fileName = file?.filename ?? '';
      const ownerType = file?.owner_name ? 'Société' : (file?.type ?? ''); //! Only company's names are returned by the api
      const ownerName = file?.owner_name ?? ''; // CompanyPage name
      const formattedDate = dateCreation ? moment(dateCreation).format('DD MMM YYYY') : null; // For some docs, documents_partages.DOC_DATE_CREATE is null
      const formattedName = getFileNameFromPath(fileName);
      const showStatus = getApprovalStatus(file);
      const isUnread = file.viewed_at === null;
      const isValidationRequired = file.validated_at === null;

      return (
        <TableRow key={idx} onClick={() => handleClickFile(file)} sx={styleRow(palette, alpha)}>
          <TableCell className="table-documents-cell-thumbnail" sx={{ px: '0 !important', width: 44 }}>
            <Stack alignItems="center">
              <FileThumbnail alt={formattedName} ext={extension} showThumbnails src={file.url} variant="neutral" />
            </Stack>
          </TableCell>
          <TableCell className="table-documents-cell-text" sx={{ p: '0px 0px 0px 10px' }}>
            <Stack alignItems="flex-start" sx={styleTextContainer}>
              <Typography noWrap sx={styleTitle(isUnread)} className="text-ellipsis" variant="body1">
                {formattedName}
              </Typography>
              {showOwner && hightDensity ? (
                <Typography className="table-documents-typography-text text-ellipsis" variant="caption">
                  <Typography component="span" noWrap sx={{ width: '100%' }} variant="caption">
                    {ownerName || ownerType}
                  </Typography>
                  {!!formattedDate && (
                    <Typography color="text.disabled" component="span" noWrap variant="caption">
                      {' '}
                      - Ajouté le {formattedDate}
                    </Typography>
                  )}
                </Typography>
              ) : (
                <>
                  {!!formattedDate && (
                    <Typography color="text.disabled" component="span" noWrap sx={{ width: '100%' }} variant="caption">
                      Ajouté le {formattedDate}
                    </Typography>
                  )}
                  {!!file.site_name && (
                    <Typography color="text.disabled" component="span" noWrap sx={{ width: '100%' }} variant="caption">
                      {file.site_name}
                    </Typography>
                  )}
                </>
              )}
              {isTablet && showStatus && <DemandStatus data={file} sx={{ mt: 0.5 }} />}
            </Stack>
          </TableCell>
          {!isTablet && hasAtLeastOnePendingDemand && (
            <TableCell className="table-documents-cell-demand-status" sx={{ width: 200 /* Chip width */ }}>
              {showStatus && <DemandStatus data={file} />}
            </TableCell>
          )}
          {showOwner && !hightDensity && (
            <TableCell className="table-documents-cell-owner-type">
              {!!ownerType && <Typography>{ownerType}</Typography>}
              {!!ownerName && (
                <Typography variant="caption" noWrap>
                  {ownerName}
                </Typography>
              )}
            </TableCell>
          )}
          {hasAtLeastOnePendingDemand && (
            <TableCell align="right" padding="none" sx={{ width: 44 /* IconButton width */ }}>
              {showStatus && (
                <DocumentAction
                  file={file}
                  name={formattedName}
                  setSelectedFile={setSelectedFile}
                  handleOpenModalConfirmCancellation={handleOpenModalConfirmCancellation}
                />
              )}
            </TableCell>
          )}
          <TableCell sx={{ p: 0, textAlign: 'end' }}>
            {(isValidationRequired || isUnread) && (
              <Chip
                label={isValidationRequired ? 'À valider' : 'Non lu'}
                color={isValidationRequired ? 'warning' : 'primary'}
                size="small"
                sx={{ borderRadius: 0 }}
              />
            )}
          </TableCell>
        </TableRow>
      );
    });
};

const styleRow = (palette, alpha) => ({
  borderBottom: `1px solid ${alpha(palette.grey[300], 0.5)}`,
  cursor: 'pointer',
  '&:hover': {
    background: alpha(palette.grey[400], 0.1)
  }
});

const styleTextContainer = {
  // width fluide avec bornes grâce à clamp(min, valeur préféré, max)
  // - 128px  : minimum — ne descend jamais en dessous
  // - 70vw-88px : valeur fluide — grandit avec la fenêtre (70% du viewport)
  //               moins ~88px réservés (icône/Chip/ect..)
  // - 384px  : maximum — ne dépasse jamais cette largeur
  width: 'clamp(128px, calc(70vw - 88px), 384px)'
};

const styleTitle = (isUnread) => ({
  fontWeight: isUnread ? 'bold' : 'normal'
});

DocumentsRows.propTypes = {
  files: PropTypes.array.isRequired,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  setSelectedFile: PropTypes.func,
  showOwner: PropTypes.bool,
  showPagination: PropTypes.bool,
  tableRef: PropTypes.object,
  handleClickFile: PropTypes.func,
  handleOpenModalConfirmCancellation: PropTypes.func
};

export default DocumentsRows;
